import { HttpErrorResponse } from '@angular/common/http';

import { errorName, TErrorName } from 'src/assets/common/enums/error-name.g';
import { IDevError } from '../dev-error/dev-error-overlay.service';

/**
 * Vorm van de JSON die de backend (ResponseError) bij een fout terugstuurt.
 * `name` is camelCase en matcht 1-op-1 met de gegenereerde errorName-enum.
 */
export interface IResponseErrorBody {
    name?: string;
    message?: string | null;
    source?: string;
    showInUI?: boolean;
    statusCode?: number;
    params?: string[];
    debug?: unknown;
}

export interface IAppError {
    name: TErrorName;
    /** Interpolatie-parameters voor de vertaalde template (errors.<name>). */
    params?: Record<string, unknown>;
    showInUI: boolean;
    status: number | null;
    duration?: number;
    offline?: boolean;
    /** Optionele fallback-tekst van de backend; de frontend vertaalt primair op `name`. */
    serverMessage?: string;
    /** Volledige debug-keten (alleen in Development aanwezig). */
    debug: IDevError | null;
}

// Client-side metadata. Voor verbindingsfouten forceert `showInUI` de notificatie,
// ongeacht wat de backend (of het ontbreken daarvan) meestuurt: de gebruiker moet
// altijd zien dat de server of database onbereikbaar is.
const CLIENT_META: Partial<Record<TErrorName, { duration?: number; offline?: boolean; showInUI?: boolean }>> = {
    [errorName.missingNetworkConnection]:  { offline: true, duration: 0, showInUI: true },
    [errorName.serverUnreachable]:         { offline: true, duration: 0, showInUI: true },
    [errorName.missingDatabaseConnection]: { offline: true, duration: 0, showInUI: true },
    [errorName.gatewayTimeout]:            { offline: true },
};

const KNOWN_NAMES = new Set<string>(Object.values(errorName));

/** Genormaliseerd foutmodel; vervangt de oude CustomError-class. */
export class AppError extends Error implements IAppError {
    override name: TErrorName;
    params?: Record<string, unknown>;
    showInUI: boolean;
    status: number | null;
    duration?: number;
    offline?: boolean;
    serverMessage?: string;
    debug: IDevError | null;

    constructor(init: Partial<IAppError> & { name: TErrorName }) {
        super(init.serverMessage ?? init.name);
        Object.setPrototypeOf(this, AppError.prototype);

        const _meta = CLIENT_META[init.name];

        this.name          = init.name;
        this.params        = init.params;
        this.showInUI      = _meta?.showInUI ?? init.showInUI ?? false;
        this.status        = init.status ?? null;
        this.serverMessage = init.serverMessage;
        this.debug         = init.debug ?? null;
        this.duration      = init.duration ?? _meta?.duration;
        this.offline       = init.offline ?? _meta?.offline;
    }
}

function nameOf(raw?: string): TErrorName {
    return raw && KNOWN_NAMES.has(raw) ? (raw as TErrorName) : errorName.internalServerError;
}

// Array-params → object met zowel `field` (eerste arg) als index-keys ({{0}}, {{1}}, ...).
function paramsToObject(params?: string[]): Record<string, unknown> | undefined {
    if (!params || params.length === 0) return undefined;

    const _obj: Record<string, unknown> = { field: params[0] };
    params.forEach((p, i) => (_obj[i] = p));
    return _obj;
}

function isResponseErrorBody(value: unknown): value is IResponseErrorBody {
    return !!value && typeof value === 'object'
        && ('showInUI' in value || 'statusCode' in value || 'name' in value);
}

function fromBody(body: IResponseErrorBody, status: number | null): AppError {
    return new AppError({
        name:          nameOf(body.name),
        params:        paramsToObject(body.params),
        showInUI:      !!body.showInUI,
        status:        body.statusCode ?? status,
        serverMessage: body.message ?? undefined,
        debug:         body.debug ? (body as unknown as IDevError) : null,
    });
}

// Non-ok response zonder ResponseError-body (bv. ASP.NET-ProblemDetails bij een model-binding-fout,
// een nginx-413, een kale 500): de gebruiker krijgt altijd feedback — klikken zonder reactie is
// geen optie. Alleen 401 blijft stil: die hoort bij de token-refresh/uitlog-flow, niet bij een actie.
function fromStatus(status: number): AppError {
    const _name: TErrorName =
        status >= 500   ? errorName.internalServerError :
        status === 403  ? errorName.missingAuthorization :
        status === 404  ? errorName.notFound :
                          errorName.badRequest;

    return new AppError({ name: _name, showInUI: status !== 401, status });
}

/**
 * Normaliseert elke input (AppError, HttpErrorResponse, ResponseError-JSON of rauwe Error)
 * naar één AppError. Een HTTP-fout zonder herkende body krijgt een melding op basis van de status
 * (zie fromStatus); overige onbekende fouten (ongevangen throws) worden internalServerError zonder UI-notificatie.
 */
export function toAppError(input: unknown): AppError {
    if (input instanceof AppError) return input;

    if (input instanceof HttpErrorResponse) {
        // showInUI komt van de backend (ResponseError): altijd true, behalve stille 401's van de
        // token-refresh/uitlog-flow. Of de servertekst meekomt staat daar los van (ExposeMessage).
        if (isResponseErrorBody(input.error)) return fromBody(input.error, input.status);

        // Status 0 = transportfout (de request kwam nooit aan). Onderscheid: is de client
        // zelf offline, of is de server onbereikbaar? showInUI wordt door CLIENT_META geforceerd.
        if (input.status === 0) {
            const _clientOffline = typeof navigator !== 'undefined' && navigator.onLine === false;
            return new AppError({
                name:   _clientOffline ? errorName.missingNetworkConnection : errorName.serverUnreachable,
                status: 0,
            });
        }
        return fromStatus(input.status);
    }

    if (isResponseErrorBody(input)) {
        return fromBody(input, input.statusCode ?? null);
    }

    return new AppError({ name: errorName.internalServerError, showInUI: false, status: null });
}
